import type { UserState } from "@/store/user/state";
import type { User } from "@/models/user";
import type { AppGroup, IngestKey } from "@/models/manager";
import type { Account } from "@/models/user";
import { decodeJwt } from "@/utils/jwt";

// Input value data structure for the setUser mutation.
interface setUserMutation {
  token?: string | undefined;
  user?: User | undefined;
  rememberMe?: boolean | undefined;
}

interface setServiceGroupsMutation {
  serviceGroups?: AppGroup[] | undefined;
}

/**
 * Update the user object for the logged-in user. This should be set to
 * null on logout.
 * @param state - Vuex state for the user module.
 * @param value - Updated value of the token and configuration to store. If
 *   rememberMe is set to true, the token will be saved in localStorage as
 *   well for use between browser sessions.
 */
export function setUser(state: UserState, value: setUserMutation): void {
  const parsedToken = decodeJwt(value.token);
  state.token = value.token || undefined;
  state.parsedToken = parsedToken;
  state.userId = parsedToken?.user;
  state.accountId = parsedToken?.acc;
  state.expiresAt = parsedToken?.exp;
  state.appGroupId = parsedToken?.grp;
  state.appGroupName = value.user?.lastAppGroupName;
  state.firstName = value.user?.firstName;
  state.lastName = value.user?.lastName;
  state.email = value.user?.email;
  state.account.billingThreshold = value.user?.account?.billingThreshold;
  state.account.isDeactivated = value.user?.account?.isDeactivated;
  state.account.packageId = value.user?.account?.packageId;
  state.account.inTrial = value.user?.account?.inTrial;
  state.account.convertedAt = value.user?.account?.convertedAt;

  if (!value.token) {
    localStorage.removeItem("userToken");
    sessionStorage.removeItem("userToken");
  } else if (value.rememberMe) {
    localStorage.setItem("userToken", value.token);
  } else {
    sessionStorage.setItem("userToken", value.token);
  }
}
/**
 * Update the available service groups for the logged-in user.
 * @param state - Vuex state for the Users module
 * @param value - New serviceGroup mutation results to be assigned to the Vuex serviceGroups state
 */
export function setServiceGroups(
  state: UserState,
  value: setServiceGroupsMutation
): void {
  state.serviceGroups = value.serviceGroups;
}

// Set the app group name for the user.
export const setAppGroupName = (state: UserState, value?: string) =>
  (state.appGroupName = value);

// Set the service group ID for the user.
export const setAppGroupId = (state: UserState, value?: string) =>
  (state.appGroupId = value);

/**
 * Update the account values for the user.
 * @param state - Vuex state for the user module.
 * @param value - The account values to update.
 */
export function updateAccountValues(state: UserState, value: Account): void {
  const updatedAccountValues = Object.assign({}, state.account, value);
  state.account = updatedAccountValues || {};
}

// Set the stripe client secret for the signup process.
/**
 * @param state - Vuex state for the user module.
 * @param value - Stripe setup intent client secret.
 */
export function setStripeSetupIntentClientSecret(
  state: UserState,
  value?: string
) {
  state.stripeSetupIntentClientSecret = value;
}
// Set the packageId for the signup process.
/**
 * @param state - Vuex state for the user module.
 * @param value - Account package id.
 */
export function setPackageId(state: UserState, value?: string) {
  state.packageId = value;
}

/**
 * Set the serviceGroup to IngestKey mapping value.
 * This state variable is used to inject the IngestKey,
 * of a given ServiceGroup, into the Docs.
 * @param state - Vuex state for the Docs module.
 * @param value - Hash mapped value with SG Keys to Array of Ingest Keys to be assigned
 * @returns - The value of the serviceGroupIngestKeyMapping state.
 */
export function setServiceGroupIngestKeyMapping(
  state: UserState,
  value: Record<string, IngestKey[]> | undefined
): void {
  state.serviceGroupIngestKeyMapping = value;
}

/**
 * Set the ID of the selected serviceGroup using the IngestKeyPicker component.
 * @param state - Vuex state for the User module.
 * @param value - ID of the selected service group.
 */
export function setServiceGroupIdSelected(
  state: UserState,
  value: string | undefined
): void {
  state.serviceGroupIdSelected = value;
}

/**
 * Set the ID of the selected ingest key using the IngestKeyPicker component.
 * @param state - Vuex state for the User module.
 * @param value - ID of the selected ingest key.
 */
export function setIngestKeyIdSelected(
  state: UserState,
  value: string | undefined
): void {
  state.ingestKeyIdSelected = value;
}
